import React, { useState, useRef, useEffect } from "react";
import { ArrowUpRight, ArrowRight, Plus, ExternalLink, ChevronsLeft } from "lucide-react";
import TopNav from "./TopNav";
import TrackModal from "./TrackModal";
import MerchModal from "./MerchModal";
import CartDrawer from "./CartDrawer";
import MerchWheel from "./MerchWheel";
import AlbumSupportModal from "./AlbumSupportModal";
import { ARTIST, ALBUM, ASSETS, TRACKS, MERCH, getSizes } from "../data/worldData";
import { apiGet, PLATFORMS, mediaUrl } from "../lib/api";

const TONE = { blue: "#243B52", ochre: "#CF9026", oxblood: "#4E0E18" };
const MARQUEE = ["ILLER", "JAMEEL NA'IM X", "AYYYEEEE", "13 TRACKS"];

const panelCls = "w-screen h-full shrink-0 overflow-y-auto bg-scroll";
const innerCls = "min-h-full w-full flex flex-col justify-center px-6 sm:px-16 py-20";

const fmtPrice = (p) => {
  if (typeof p === "string") return p;
  const n = Number(p);
  return "$" + (Number.isInteger(n) ? String(n) : n.toFixed(2));
};

export default function Experience() {
  const [track, setTrack] = useState(null);
  const [merchItem, setMerchItem] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [shopMode, setShopMode] = useState("wheel"); // wheel (default) | grid
  const [tracks, setTracks] = useState(TRACKS);
  const [merch, setMerch] = useState(MERCH);
  const [albumLinks, setAlbumLinks] = useState({});
  const [showHint, setShowHint] = useState(true);
  const [supportOpen, setSupportOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    apiGet("/tracks").then((d) => { if (Array.isArray(d) && d.length) setTracks(d); }).catch(() => {});
    apiGet("/merch").then((d) => {
      if (Array.isArray(d) && d.length) setMerch(d.map((m) => ({ ...m, price: fmtPrice(m.price), img: mediaUrl(m.img) })));
    }).catch(() => {});
    apiGet("/settings").then((d) => setAlbumLinks(d.album_links || {})).catch(() => {});
  }, []);

  const mergeAdd = (prev, item, size, qty) => {
    const lineId = size ? `${item.id}__${size}` : item.id;
    const found = prev.find((p) => p.lineId === lineId);
    if (found) return prev.map((p) => (p.lineId === lineId ? { ...p, qty: Math.min(99, p.qty + qty) } : p));
    return [...prev, { ...item, qty, size: size || null, lineId }];
  };

  const addToCart = (item, { size = null, qty = 1 } = {}) => {
    setCart((prev) => mergeAdd(prev, item, size, qty));
    setCartOpen(true);
  };
  const removeFromCart = (lineId) => setCart((prev) => prev.filter((p) => p.lineId !== lineId));
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  // Wheel adds silently (no drawer jump); clothing opens the detail modal to pick a size
  const addFromWheel = (item, { size = null, qty = 1 } = {}) => {
    if (getSizes(item).length && !size) { setMerchItem(item); return; }
    setCart((prev) => mergeAdd(prev, item, size, qty));
  };

  const albumAvailable = PLATFORMS.filter((p) => albumLinks[p.key] && albumLinks[p.key].trim());

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      const panel = e.target.closest("[data-panel]");
      if (panel && panel.scrollHeight > panel.clientHeight + 2) {
        const atTop = panel.scrollTop <= 0;
        const atBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 1;
        const goingUp = e.deltaY < 0;
        const goingDown = e.deltaY > 0;
        if (!((goingUp && atTop) || (goingDown && atBottom))) return;
      }
      if (Math.abs(e.deltaY) >= Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    const onScroll = () => { if (el.scrollLeft > 60) setShowHint(false); };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <>
      <TopNav scrollRef={scrollRef} cartCount={cartCount} onCart={() => setCartOpen(true)} />
      <div
        ref={scrollRef}
        data-testid="h-scroller"
        className="fixed inset-0 z-10 flex overflow-x-auto overflow-y-hidden bg-scroll grain text-[#15100E]"
      >
        {/* HERO */}
        <section id="home" data-panel className={panelCls}>
          <div className={`${innerCls} pt-24`}>
            <div className="grid lg:grid-cols-12 gap-8 items-center w-full max-w-[1500px] mx-auto">
              <div className="lg:col-span-7">
                <h1 className="font-display font-extrabold stretch-text leading-[0.8] text-[13vw] lg:text-[8rem] animate-fade-up" style={{ animationDelay: "0.08s" }}>
                  JAMEEL<br />NA'IM<span className="text-[#CF9026]"> X</span>
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-6 animate-fade-up" style={{ animationDelay: "0.2s" }}>
                  <span className="font-tech text-sm tracking-[0.25em] bg-[#15100E] text-[#ECE7DB] px-4 py-2">{ALBUM}</span>
                  <a
                    data-testid="buy-album-btn"
                    href="https://untitled.stream/buy/project/8pM2Dr4pAF3OkBda402Jq"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 font-tech text-sm tracking-[0.25em] bg-[#7A1623] text-[#ECE7DB] px-5 py-2.5 hover:bg-[#CF9026] hover:text-[#15100E] transition-colors"
                  >
                    BUY THE ALBUM <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </a>
                </div>
                <button
                  data-testid="hero-name-price-link"
                  onClick={() => setSupportOpen(true)}
                  className="group mt-3 inline-flex items-center gap-2 font-tech text-sm tracking-[0.25em] bg-[#7A1623] text-[#ECE7DB] px-5 py-2.5 hover:bg-[#CF9026] hover:text-[#15100E] transition-colors animate-fade-up"
                  style={{ animationDelay: "0.24s" }}
                >
                  NON US? BUY NOW <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
                {albumAvailable.length > 0 && (
                  <div className="mt-5 animate-fade-up" style={{ animationDelay: "0.3s" }}>
                    <p className="font-tech text-[#15100E]/50 text-[10px] tracking-[0.3em] mb-2">STREAM THE ALBUM</p>
                    <div className="flex flex-wrap gap-2">
                      {albumAvailable.map((p) => (
                        <a
                          key={p.key}
                          data-testid={`album-link-${p.key}`}
                          href={albumLinks[p.key]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 border-2 border-[#15100E] px-3 py-2 font-tech text-[10px] tracking-[0.15em] hover:bg-[#CF9026] transition-colors"
                        >
                          {p.label.toUpperCase()} <ExternalLink size={12} className="opacity-60" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={() => document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" })} className="mt-8 flex items-center gap-2 font-tech text-xs tracking-[0.25em] text-[#15100E]/60 hover:text-[#7A1623] transition-colors animate-fade-up" style={{ animationDelay: "0.4s" }}>
                  SCROLL RIGHT <ArrowRight size={14} className="animate-pulse" />
                </button>
              </div>
              <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "0.3s" }}>
                <div data-testid="hero-video" className="mb-4 border-2 border-[#15100E] edge overflow-hidden bg-black" style={{ aspectRatio: "16 / 9" }}>
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/3-dqgaY1k70?rel=0&modestbranding=1&playsinline=1"
                    title="ILLER video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
                <button
                  type="button"
                  data-testid="cover-flip"
                  onClick={() => setFlipped((f) => !f)}
                  className="group relative block w-full edge border-2 border-[#15100E] overflow-hidden cursor-pointer"
                >
                  <img src={ASSETS.cover} alt="ILLER front cover" className={`w-full max-h-[60vh] object-cover transition-opacity duration-500 ${flipped ? "opacity-0" : "opacity-100"}`} />
                  <img src={ASSETS.back} alt="ILLER back cover — tracklist" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${flipped ? "opacity-100" : "opacity-0"}`} />
                  <span className="absolute bottom-3 left-4 font-tech text-[#ECE7DB] text-xs tracking-widest mix-blend-difference">ILLER · {flipped ? "FIG.02" : "FIG.01"}</span>
                  <span className="absolute top-3 right-3 font-tech text-[9px] tracking-[0.2em] bg-[#15100E] text-[#ECE7DB] px-2 py-1 opacity-80 group-hover:opacity-100 transition-opacity">TAP TO FLIP</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* VERTICAL MARQUEE DIVIDER */}
        <div className="shrink-0 h-full w-14 sm:w-16 bg-[#15100E] overflow-hidden flex items-center justify-center">
          <div className="rotate-90 whitespace-nowrap flex animate-marquee">
            {[0, 1].map((k) => (
              <span key={k} className="flex items-center">
                {MARQUEE.map((m, i) => (
                  <span key={i} className="font-display font-extrabold text-lg sm:text-xl px-6 text-[#ECE7DB] flex items-center gap-6">
                    {m} <span className="text-[#CF9026]">✦</span>
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>

        {/* GALLERY / THE STORY */}
        <section id="gallery" data-panel className={panelCls}>
          <div className={`${innerCls} items-center`}>
            <div className="w-full max-w-3xl mx-auto text-center">
              <p className="font-tech text-[#7A1623] tracking-[0.35em] text-xs mb-3">THE STORY</p>
              <h2 className="font-display font-extrabold stretch-text leading-[0.8] text-[18vw] lg:text-[10rem]">iLLER</h2>
              <div className="mt-8 text-left space-y-5">
                <p className="font-body text-[#15100E]/80 text-sm sm:text-base leading-relaxed">
                  The practice, the time, the mistakes, the setbacks, the hunger, the missed opportunities. All of these trials &amp; tribulations led up to this defining moment. <span className="font-tech">iLLER</span> is a compilation of thoughts, visions, doubts, fears, struggles, joys, vices, growing pains.
                </p>
                <blockquote className="border-l-4 border-[#7A1623] pl-5 py-1">
                  <p className="font-display font-bold text-[#15100E] text-lg sm:text-2xl leading-snug stretch-text">
                    “This project is the closest I've came to perfection. This is my Thriller. This is the most complete body of work I've ever created..”
                  </p>
                  <cite className="font-tech text-[#7A1623] text-[11px] tracking-[0.25em] mt-2 block not-italic">— JNX</cite>
                </blockquote>
                <p className="font-body text-[#15100E]/80 text-sm sm:text-base leading-relaxed">
                  In JNX's discography the cover art explains everything for you. It is a picture of Michael Jackson performing his iconic toe-stance dance move during the time he performed Beat It for the first time. We as an audience saw perfection, but to Michael he saw mistakes that needed to be corrected. This album has no mistakes, no room for correction — it is simply beautiful. And with that being said, let it speak for itself.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* MUSIC / TRACKLIST */}
        <section id="music" data-panel className={`${panelCls} bg-[#15100E] text-[#ECE7DB]`}>
          <div className={innerCls}>
            <div className="w-full max-w-[1500px] mx-auto">
              <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <h2 className="font-display font-extrabold stretch-text text-5xl sm:text-6xl leading-none">TRACKLIST</h2>
                <p className="font-tech text-[#ECE7DB]/50 text-xs tracking-[0.2em]">{tracks.length} SONGS · TAP A TITLE</p>
              </div>
              <div className="grid md:grid-cols-2 md:gap-x-12 border-t-2 border-[#ECE7DB]/20">
                {tracks.map((t) => (
                  <button
                    key={t.n}
                    data-testid={`track-row-${t.n}`}
                    onClick={() => setTrack(t)}
                    className="group w-full flex items-center gap-4 py-2.5 border-b-2 border-[#ECE7DB]/15 text-left hover:bg-[#CF9026] hover:text-[#15100E] hover:px-3 transition-all duration-300"
                  >
                    <span className="font-tech text-xs w-7 shrink-0 opacity-60">{String(t.n).padStart(2, "0")}</span>
                    <span className="font-display font-extrabold stretch-text text-xl sm:text-2xl flex-1 leading-none truncate">{t.title}</span>
                    {t.feat && <span className="font-tech text-[9px] tracking-widest hidden lg:block opacity-70">FEAT. {t.feat}</span>}
                    <ArrowUpRight size={18} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SHOP — MERCH STAND */}
        <section id="shop" data-panel className={panelCls}>
          <div className={innerCls}>
            <div className="w-full max-w-[1500px] mx-auto">
              <div className="flex items-end justify-between mb-2 flex-wrap gap-3">
                <div>
                  <p className="font-tech text-[#7A1623] tracking-[0.35em] text-xs mb-2">ILLER × OTB ENT.</p>
                  <h2 className="font-display font-extrabold stretch-text text-5xl sm:text-7xl leading-none">MERCH STAND</h2>
                </div>
                <button data-testid="shop-open-cart" onClick={() => setCartOpen(true)} className="font-tech text-xs tracking-[0.2em] bg-[#15100E] text-[#ECE7DB] px-4 py-2 hover:bg-[#4E0E18] transition-colors">
                  VIEW CART ({cartCount})
                </button>
              </div>
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <p className="font-tech text-[#15100E]/55 text-xs tracking-[0.2em]">STEP UP TO THE TABLE · TAP AN ITEM · WHILE SUPPLIES LAST</p>
                <div className="flex items-center border-2 border-[#15100E]">
                  <button
                    data-testid="shop-mode-wheel"
                    onClick={() => setShopMode("wheel")}
                    className={`font-tech text-[10px] tracking-[0.2em] px-3 py-2 transition-colors ${shopMode === "wheel" ? "bg-[#CF9026] text-[#15100E]" : "bg-transparent text-[#15100E]/60 hover:text-[#15100E]"}`}
                  >
                    ◎ WHEEL
                  </button>
                  <button
                    data-testid="shop-mode-grid"
                    onClick={() => setShopMode("grid")}
                    className={`font-tech text-[10px] tracking-[0.2em] px-3 py-2 border-l-2 border-[#15100E] transition-colors ${shopMode === "grid" ? "bg-[#CF9026] text-[#15100E]" : "bg-transparent text-[#15100E]/60 hover:text-[#15100E]"}`}
                  >
                    ▦ GRID
                  </button>
                </div>
              </div>

              {shopMode === "wheel" ? (
                <MerchWheel
                  variant="inline"
                  items={merch.filter((m) => m.available !== false)}
                  onAdd={addFromWheel}
                  onItemClick={(it) => setMerchItem(it)}
                  requiresSize={(it) => getSizes(it).length > 0}
                />
              ) : (
              <div className="relative">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {merch.map((m) => {
                    const soldOut = m.available === false;
                    return (
                      <div key={m.id} className="flex flex-col">
                        <button
                          data-testid={`merch-item-${m.id}`}
                          onClick={() => setMerchItem(m)}
                          className="group text-left border-2 border-[#15100E] overflow-hidden hover:-translate-y-2 transition-transform duration-300 bg-[#ECE7DB]"
                        >
                          <div className="relative overflow-hidden" style={{ height: "min(34vh, 280px)", background: TONE[m.tone] }}>
                            <img src={m.img} alt={m.name} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${soldOut ? "opacity-40 grayscale" : ""}`} />
                            <span className="absolute top-2 left-2 font-tech text-[#15100E] bg-[#CF9026] text-[9px] tracking-widest px-1.5 py-0.5">{m.note}</span>
                            {soldOut && (
                              <span data-testid={`merch-soldout-${m.id}`} className="absolute inset-0 flex items-center justify-center font-display font-extrabold text-[#ECE7DB] text-2xl tracking-widest" style={{ background: "rgba(21,16,14,0.55)" }}>
                                SOLD OUT
                              </span>
                            )}
                            {!soldOut && <span className="absolute bottom-2 right-2 h-8 w-8 bg-[#15100E] text-[#ECE7DB] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Plus size={16} /></span>}
                          </div>
                          <div className="p-3 flex items-center justify-between">
                            <p className="font-display font-extrabold text-sm sm:text-base leading-none">{m.name}</p>
                            <span className="font-tech text-[#7A1623] text-sm shrink-0 ml-2">{m.price}</span>
                          </div>
                        </button>
                        <button
                          data-testid={`merch-quickadd-${m.id}`}
                          disabled={soldOut}
                          onClick={() => (getSizes(m).length ? setMerchItem(m) : addToCart(m))}
                          className="mt-2 font-tech text-[11px] tracking-[0.2em] text-[#ECE7DB] bg-[#15100E] py-2.5 hover:bg-[#4E0E18] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {soldOut ? "SOLD OUT" : getSizes(m).length ? "SELECT SIZE" : "+ ADD TO CART"}
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="h-5 sm:h-6 bg-[#15100E]" style={{ boxShadow: "0 14px 24px rgba(21,16,14,0.35)" }} />
                <div className="h-2 bg-[#7A1623]" />
              </div>
              )}
            </div>
          </div>
        </section>

        {/* FOOTER PANEL */}
        <section data-panel className={`${panelCls} bg-[#4E0E18] text-[#ECE7DB]`}>
          <div className={innerCls}>
            <div className="w-full max-w-[1400px] mx-auto grid sm:grid-cols-[1fr_auto] gap-8 items-end">
              <div>
                <h3 className="font-display font-extrabold stretch-text text-6xl sm:text-8xl leading-[0.82]">ILLER</h3>
                <a
                  data-testid="footer-buy-album-btn"
                  href="https://untitled.stream/buy/project/8pM2Dr4pAF3OkBda402Jq"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-6 inline-flex items-center gap-2 font-tech text-sm tracking-[0.25em] bg-[#CF9026] text-[#15100E] px-6 py-3 hover:bg-[#ECE7DB] transition-colors"
                >
                  BUY THE ALBUM <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
                <button
                  data-testid="footer-name-price-link"
                  onClick={() => setSupportOpen(true)}
                  className="group mt-3 inline-flex items-center gap-2 font-tech text-sm tracking-[0.25em] bg-[#7A1623] text-[#ECE7DB] px-5 py-2.5 hover:bg-[#CF9026] hover:text-[#15100E] transition-colors"
                >
                  NON US? BUY NOW <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
                <p className="font-tech text-[#ECE7DB]/60 text-xs tracking-widest mt-6">© {new Date().getFullYear()} {ARTIST} — ALL RIGHTS RESERVED</p>
              </div>
              <img src={ASSETS.artist} alt={ARTIST} className="w-32 sm:w-44 border-2 border-[#ECE7DB] film justify-self-end" />
            </div>
          </div>
        </section>
      </div>

      {showHint && (
        <button
          type="button"
          data-testid="swipe-hint"
          onClick={() => { scrollRef.current && (scrollRef.current.scrollLeft += window.innerWidth * 0.9); setShowHint(false); }}
          className="fixed right-4 sm:right-6 top-1/2 -translate-y-1/2 z-[60] flex flex-col items-center gap-2 pointer-events-auto"
          aria-label="Swipe left to explore"
        >
          <div className="animate-hint-flash flex items-center gap-1 bg-[#15100E] text-[#ECE7DB] px-2.5 py-2.5 rounded-full">
            <ChevronsLeft size={18} className="animate-swipe-nudge text-[#CF9026]" />
          </div>
          <span className="font-tech text-[8px] tracking-[0.25em] text-[#15100E] bg-[#CF9026] px-1.5 py-0.5 whitespace-nowrap">SWIPE LEFT</span>
        </button>
      )}

      <TrackModal track={track} onClose={() => setTrack(null)} />
      <MerchModal item={merchItem} onClose={() => setMerchItem(null)} onAdd={addToCart} />
      <CartDrawer open={cartOpen} items={cart} onClose={() => setCartOpen(false)} onRemove={removeFromCart} />
      <AlbumSupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
}
